/**
 * ConfirmEmail.js — Public landing page hit from the Postmark
 * "Confirm your HiBFF email" link. Calls the backend, shows a
 * success / error state, then routes the user to their dashboard
 * (if logged-in) or to login.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConfirmEmail = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | success | already | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setState('error');
      setMessage('Missing confirmation token.');
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/auth/confirm-email?token=${encodeURIComponent(token)}`);
        const data = await r.json();
        if (!r.ok) {
          setState('error');
          setMessage(data.detail || 'Could not confirm your email.');
          return;
        }
        if (data.status === 'already_confirmed') {
          setState('already');
        } else {
          setState('success');
        }
      } catch (e) {
        setState('error');
        setMessage('Network error. Please try again.');
      }
    })();
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#F9F7F2' }}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center" data-testid="confirm-email-page">
        {state === 'loading' && (
          <div className="space-y-4">
            <Loader2 className="w-10 h-10 animate-spin mx-auto" style={{ color: '#F5831F' }} />
            <p className="text-sm" style={{ color: '#666666' }}>Confirming your email…</p>
          </div>
        )}
        {state === 'success' && (
          <div className="space-y-4" data-testid="confirm-email-success">
            <CheckCircle2 className="w-14 h-14 mx-auto" style={{ color: '#2EAA5C' }} />
            <h2 className="text-2xl font-bold" style={{ color: '#1A1209' }}>Email confirmed</h2>
            <p className="text-sm" style={{ color: '#666666' }}>
              Your HiBFF account is all set. You can now sign in and start practising
              with your AvA.
            </p>
            <button
              data-testid="confirm-email-cta-login"
              onClick={() => navigate('/login')}
              className="w-full py-3 rounded-lg text-white font-medium"
              style={{ backgroundColor: '#F5831F' }}
            >
              Sign in to HiBFF
            </button>
          </div>
        )}
        {state === 'already' && (
          <div className="space-y-4" data-testid="confirm-email-already">
            <CheckCircle2 className="w-14 h-14 mx-auto" style={{ color: '#2EAA5C' }} />
            <h2 className="text-2xl font-bold" style={{ color: '#1A1209' }}>Already confirmed</h2>
            <p className="text-sm" style={{ color: '#666666' }}>
              This email has already been confirmed. You&apos;re good to go.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 rounded-lg text-white font-medium"
              style={{ backgroundColor: '#F5831F' }}
            >
              Sign in
            </button>
          </div>
        )}
        {state === 'error' && (
          <div className="space-y-4" data-testid="confirm-email-error">
            <AlertCircle className="w-14 h-14 mx-auto" style={{ color: '#DC2626' }} />
            <h2 className="text-2xl font-bold" style={{ color: '#1A1209' }}>Confirmation failed</h2>
            <p className="text-sm" style={{ color: '#666666' }}>{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 rounded-lg text-white font-medium"
              style={{ backgroundColor: '#F5831F' }}
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmEmail;
